import heroImage from "./article_pics/goat_and_worm.jpg";

const article = {
    title: "From EarthWorm to Pygmy Goat: Tightening 6 Byte Suricata Rules and Solving Pool Matching",
    published: "2026-05-17",
    readingTime: "14 min read",
    tags: ["Suricata", "Detection Engineering", "PCAP", "Threat Emulation", "EarthWorm"],
    summary:
        "This article tightens EarthWorm back to exact 6 byte control records, then walks through packet generation, PCAP collection, failed pure rule attempts, and the final split 2 Lua solution for Pygmy Goat pool matching.",
    favorite: false,
    image: heroImage,
    repoUrl: "https://github.com/Pb-22/Network_Detections/tree/main/Pygmy-Goat-Earthworm",
    content: `The first round of EarthWorm work produced a local reverse SOCKS lab with repeatable traffic, controlled PCAP collection, and clean separation between setup stage and request stage behavior. That baseline made it possible to tighten the Suricata rules and then extend the request stage logic for the Pygmy Goat pool variant.

The original EarthWorm lab article is here: [Building an EarthWorm Reverse SOCKS Lab](/articles/earthworm-lab).

This article covers that progression:

- tightening the vanilla EarthWorm rules back to exact 6 byte records
- improving rule performance by replacing busy multistage 2 byte matching with cleaner anchors
- modifying the packet creation tooling to generate pool based request stage cases
- collecting the PCAP set used for validation
- testing pure rule approaches and seeing where they failed
- trying a 1 Lua solution
- moving to the final split 2 Lua solution

## Exact 6 byte EarthWorm records

The earlier EarthWorm rules were already limiting false signals, but the combination of multiple 2 byte matches still made the ruleset do more work than necessary. The early anchors matched often. Alert quality stayed good because later stages filtered the results, but the performance profile still needed improvement.

The cleaner direction was to anchor on the exact 6 byte control records seen in the PCAPs.

### Setup stage records

The setup stage consistently included:

- \`01 01 00 00 00 00\`
- \`01 02 00 00 00 00\`
- often \`01 03 00 00 00 00\`

The vanilla rules used for this path were:

\`\`\`suricata
# EarthWorm candidate ET follow-up set: vanilla-focused
# restoring the full 6-byte zero-tail records for the exact vanilla EarthWorm path.
#
# Current intent:
# - improve specificity/performance on the plain EarthWorm records
# - preserve the optional 01 03 setup-stage pool assignment indicator
# - keep request-stage detection aligned to the observed six-byte control records

alert tcp-pkt any any -> any any (msg:"ET MALWARE EARTHWORM SOCKS Reverse Proxy Initial Setup Request Vanilla"; flow:established,to_server; flowbits:set,ET.earthworm.setup.stage1; flowbits:noalert; dsize:6; content:"|01 01 00 00 00 00|"; fast_pattern; startswith; reference:url,community.emergingthreats.net/t/sig-earthworm-reverse-socks-handshake-and-tunnel-sequence-detection/3314; classtype:trojan-activity; sid:9000011; rev:1;)

alert tcp-pkt any any -> any any (msg:"ET MALWARE EARTHWORM SOCKS Reverse Proxy Server Response Vanilla"; flow:established,to_client; flowbits:isset,ET.earthworm.setup.stage1; flowbits:set,ET.earthworm.setup.confirmed; dsize:6; content:"|01 02 00 00 00 00|"; fast_pattern; startswith; reference:url,community.emergingthreats.net/t/sig-earthworm-reverse-socks-handshake-and-tunnel-sequence-detection/3314; classtype:trojan-activity; sid:9000012; rev:1;)

alert tcp-pkt any any -> any any (msg:"ET MALWARE EARTHWORM SOCKS Reverse Proxy Assign Pool Number Request"; flow:established,to_client; flowbits:isset,ET.earthworm.setup.confirmed; dsize:6; content:"|01 03|"; fast_pattern; startswith; reference:url,community.emergingthreats.net/t/sig-earthworm-reverse-socks-handshake-and-tunnel-sequence-detection/3314; classtype:trojan-activity; sid:9000013; rev:1;)

alert tcp-pkt any any -> any any (msg:"ET MALWARE EARTHWORM SOCKS Reverse Proxy Tunnel Request Vanilla"; flow:established,to_server; flowbits:set,ET.earthworm.request.stage1; flowbits:noalert; dsize:6; content:"|01 04 00 00 00 00|"; fast_pattern; startswith; reference:url,community.emergingthreats.net/t/sig-earthworm-reverse-socks-handshake-and-tunnel-sequence-detection/3314; classtype:trojan-activity; sid:9000014; rev:1;)

alert tcp-pkt any any -> any any (msg:"ET MALWARE EARTHWORM SOCKS Proxy Tunnel Response Vanilla"; flow:established,to_client; flowbits:isset,ET.earthworm.request.stage1; flowbits:set,ET.earthworm.request.stage2; flowbits:noalert; dsize:6; content:"|01 05 00 00 00 00|"; fast_pattern; startswith; reference:url,community.emergingthreats.net/t/sig-earthworm-reverse-socks-handshake-and-tunnel-sequence-detection/3314; classtype:trojan-activity; sid:9000015; rev:1;)

alert tcp-pkt any any -> any any (msg:"ET MALWARE EARTHWORM SOCKS Proxy Tunnel Post Setup Request Vanilla"; flow:established,to_client; flowbits:isset,ET.earthworm.request.stage2; dsize:4; content:"|05 02 00 01|"; fast_pattern; reference:url,community.emergingthreats.net/t/sig-earthworm-reverse-socks-handshake-and-tunnel-sequence-detection/3314; classtype:trojan-activity; sid:9000016; rev:1;)
\`\`\`

The setup stage and request stage both moved back to full 6 byte records. The optional \`01 03\` setup stage assignment record stayed in place. That kept the vanilla rules specific and reduced the need for smaller chained matches.

The Pygmy Goat work only needed changes in the request stage rules because the setup stage stayed aligned with the simplified EarthWorm baseline.

![Vanilla EarthWorm rules compared with the Pygmy Goat request-stage path](${require("./article_pics/earthworm_rule_comparison.png")})

*Vanilla EarthWorm benefits from exact fixed records. Pygmy Goat adds a pool equality problem in the request stage.*

## ET review changes that improved the rules

Other improvements were made during rule review and testing. These are worth calling out because they teach more than formatting alone.

### \`tcp-pkt\` instead of \`tcp\`

The reviewed vanilla rules use \`tcp-pkt\` rather than plain \`tcp\`. Suricata documents \`tcp-pkt\` as matching content in individual TCP packets, while \`tcp-stream\` is for matching only in the reassembled stream. In Suricata's rule categorization, \`tcp-pkt\` is a packet rule and is inspected on a per packet basis. That fits these EarthWorm records well because the detection target is a short fixed record at the packet payload level, not a long stream pattern that benefits from reassembly.

A small average tick improvement was observed in some tests when the six PCAPs were run together, so \`tcp-pkt\` became the better fit for this rule family.

### \`startswith\` instead of \`depth\`

Suricata documents \`startswith\` as a shorthand for matching a content pattern exactly at the start of a buffer. It is equivalent to \`offset:0\` plus the matching \`depth\` for that pattern.

That makes the EarthWorm records easier to read. The rule is not just looking near the beginning of the payload. It is saying the control record begins at byte zero. For short 6 byte records like \`01 01 00 00 00 00\` and \`01 04 00 00 00 00\`, \`startswith\` expresses the intent directly.

### explicit \`fast_pattern\`

Suricata can choose a fast pattern automatically. The docs explain that if \`fast_pattern\` is not set, Suricata selects one based on buffer priority, pattern length, and pattern strength. The ET reviewed rules set \`fast_pattern\` explicitly anyway.

That makes sense here for two reasons:

- it removes ambiguity about which content Suricata should promote for prefiltering
- a small CPU benefit was observed in some tests when the fast pattern was set explicitly

For this rule family, the combination makes the design tighter:

- \`tcp-pkt\` keeps matching on the packet payload where these control records live
- \`startswith\` anchors the record at byte zero
- \`fast_pattern\` tells Suricata exactly which content should drive the early match work

Those adjustments did not change the EarthWorm story on the wire. They changed how efficiently Suricata could express and evaluate that story.

## The Pygmy Goat request stage

The Pygmy Goat material changed the request stage by carrying a pool value inside the control records. Instead of all zeroes, the request stage could include values tied to a specific pool.

![Pygmy Goat payload structure from the whitepaper](${require("./article_pics/pygmy_goat_pool_structure.png")})

*Whitepaper payload structure figure showing the pool value inside the \`01 03\`, \`01 04\`, and \`01 05\` control records.*

The whitepaper figure shows the pool value inside the \`01 03\`, \`01 04\`, and \`01 05\` control records.

That changed the detection target.

The request stage rule now needed to establish all of the following:

- a \`01 04\` record appeared
- its trailing 4 bytes contained a pool value
- a \`01 05\` record followed
- the \`01 05\` record used the same pool value
- the sequence then moved into the expected SOCKS request bytes

## Modifying the packet creation tooling

The whitepaper gave the structure, but traffic was still needed to exercise the rule logic cleanly. That meant modifying and testing the EarthWorm packet creation tooling so controlled request stage records could be generated with chosen pool values.

That step made the rest of the work possible.

Once the packet generation path produced the right variants, test cases could be built deliberately instead of inferred from the diagram alone. The lab also became more useful because the same sequences could be reproduced repeatedly and compared across positive and negative controls.

## The PCAP set

After the packet creation work, a focused PCAP set was collected for the pool aware cases:

- **EW7** = \`ew-test-07-pool-disabled-zero.pcap\`
- **EW8** = \`ew-test-08-pool-enabled-04d2.pcap\`
- **EW9** = \`ew-test-09-pool-enabled-1337.pcap\`
- **EW10** = \`ew-test-10-pool-mismatch-04d2-1337.pcap\`

These became the shorthand for the rest of the testing.

Expected results:

- **EW7** = no alert
- **EW8** = alert
- **EW9** = alert
- **EW10** = no alert

EW7 stayed useful as the zero pool baseline.

EW8 and EW9 showed that the rule could alert on different non zero pool values.

EW10 was the hard check. It used mismatched pool values and forced the rule to prove that it was checking equality correctly.

![EW7 through EW10 validation matrix](${require("./article_pics/ew7_ew10_test_matrix.png")})

*EW10 is the negative control. Any rule that alerts on EW10 is still too broad.*

## What was tried with Suricata rules

The first passes stayed in Suricata rule language.

The obvious things to try were:

- match \`01 04\`
- test that the trailing bytes were non zero
- match \`01 05\`
- continue into the SOCKS request bytes
- use byte oriented logic where possible to keep the rule set compact

The validated Pygmy Goat request stage rules used for this path were:

\`\`\`suricata
alert tcp-pkt any any -> any any (msg:"ET MALWARE EARTHWORM SOCKS Reverse Proxy Tunnel Request Pool Store"; flow:established,to_server; dsize:6; content:"|01 04|"; startswith; byte_test:4,>,0,2; byte_extract:4,2,ew_req_pool; lua:lua/earthworm_pool_store.lua; xbits:set,ET.earthworm.request.pool.stage1,track ip_pair,expire 300; noalert; classtype:trojan-activity; sid:9000031; rev:3;)

alert tcp-pkt any any -> any any (msg:"ET MALWARE EARTHWORM SOCKS Reverse Proxy Tunnel Response Pool Match"; flow:established,to_client; xbits:isset,ET.earthworm.request.pool.stage1,track ip_pair; dsize:6; content:"|01 05|"; startswith; byte_test:4,>,0,2; byte_extract:4,2,ew_resp_pool; lua:lua/earthworm_pool_compare.lua; xbits:set,ET.earthworm.request.pool.stage2,track ip_pair,expire 300; noalert; classtype:trojan-activity; sid:9000032; rev:3;)

alert tcp-pkt any any -> any any (msg:"ET MALWARE EARTHWORM SOCKS Proxy Tunnel Post Setup Request Pool Match"; flow:established,to_client; xbits:isset,ET.earthworm.request.pool.stage2,track ip_pair; dsize:4; content:"|05 02 00 01|"; fast_pattern; classtype:trojan-activity; sid:9000033; rev:1;)
\`\`\`

These rules kept the request stage and SOCKS transition in view, but the actual goal was equality between the pool in \`01 04\` and the pool in \`01 05\`.

## Where the pure rule approaches failed

EW10 made the failure easy to see.

EW10 used mismatched pool values:

- \`01 04 04 d2\`
- \`01 05 13 37\`

This should not alert.

Some of the earlier rule ideas still alerted on EW10. They recognized the general request stage shape. They recognized non zero pool values. They recognized the SOCKS transition, but they did not enforce pool equality strongly enough.

That was the key failure.

A rule that alerts on EW8 and EW9 but also alerts on EW10 is still too broad. It is close, but it is not correct.

## The 1 Lua solution

The next step was Lua.

The 1 Lua direction handled the pool logic inside a single script:

- inspect the \`01 04\` record
- store the pool value
- inspect the \`01 05\` record
- compare the values
- allow the rule path to continue only when the values matched

That approach proved the pool comparison belonged in stateful logic and reduced awkward byte tests.

A compact example of that style looks like this:

\`\`\`lua
function init(args)
    local needs = {}
    needs["payload"] = tostring(true)
    return needs
end

function match(args)
    local payload = tostring(args["payload"] or "")

    local a, b, c, d = payload:match("\x01\x04(.)(.)(.)(.).*\x01\x05(.)(.)(.)(.)")
    if not a then
        return 0
    end

    local e, f, g, h = payload:match("\x01\x04(.)(.)(.)(.).*\x01\x05(.)(.)(.)(.)")
    if a == e and b == f and c == g and d == h then
        return 1
    end

    return 0
end
\`\`\`

The example above shows the general idea, but spoiler alert: it failed. This one script tries to see both records and compare the captured bytes in one place.

That structure worked as a stepping stone, but it was not the cleanest final shape. The script had to do too much at once, and the state handling became harder to reason about.

## The split 2 Lua solution

The next improvement was to split the work into two scripts.

The first script handles the \`01 04\` stage and stores the candidate pool value.

The second script handles the \`01 05\` stage and compares that value against what was stored earlier.

In the final validated path, the rules stay stage-aware and the Lua helpers only handle state storage and numeric comparison:

\`\`\`suricata
alert tcp-pkt any any -> any any (msg:"ET MALWARE EARTHWORM SOCKS Reverse Proxy Tunnel Request Pool Store"; flow:established,to_server; dsize:6; content:"|01 04|"; startswith; byte_test:4,>,0,2; byte_extract:4,2,ew_req_pool; lua:lua/earthworm_pool_store.lua; xbits:set,ET.earthworm.request.pool.stage1,track ip_pair,expire 300; noalert; classtype:trojan-activity; sid:9000031; rev:3;)

alert tcp-pkt any any -> any any (msg:"ET MALWARE EARTHWORM SOCKS Reverse Proxy Tunnel Response Pool Match"; flow:established,to_client; xbits:isset,ET.earthworm.request.pool.stage1,track ip_pair; dsize:6; content:"|01 05|"; startswith; byte_test:4,>,0,2; byte_extract:4,2,ew_resp_pool; lua:lua/earthworm_pool_compare.lua; xbits:set,ET.earthworm.request.pool.stage2,track ip_pair,expire 300; noalert; classtype:trojan-activity; sid:9000032; rev:3;)

alert tcp-pkt any any -> any any (msg:"ET MALWARE EARTHWORM SOCKS Proxy Tunnel Post Setup Request Pool Match"; flow:established,to_client; xbits:isset,ET.earthworm.request.pool.stage2,track ip_pair; dsize:4; content:"|05 02 00 01|"; fast_pattern; classtype:trojan-activity; sid:9000033; rev:1;)
\`\`\`

### \`earthworm_pool_store.lua\`

The first script stores the request side pool value from the \`01 04\` stage.

It uses Suricata's \`bytevar\` support to read the mapped pool value and \`flowint\` support to persist that value for later comparison.

\`\`\`lua
local bytevarlib = require("suricata.bytevar")
local flowintlib = require("suricata.flowint")

function init(sig)
 bytevarlib.map(sig, "ew_req_pool")
 flowintlib.register("ew_pool_req04")
 local needs = {}
 needs["payload"] = tostring(true)
 return needs
end

function thread_init()
 ew_req_pool = bytevarlib.get("ew_req_pool")
 ew_pool_req04 = flowintlib.get("ew_pool_req04")
end

function match(args)
 local req_pool = ew_req_pool:value()
 if req_pool ~= nil then
 ew_pool_req04:set(req_pool)
 return 1
 end
 return 0
end
\`\`\`

A few details are worth calling out:

- \`bytevarlib.map(sig, "ew_req_pool")\` binds the script to the bytevar extracted by the rule
- \`flowintlib.register("ew_pool_req04")\` creates the flow scoped storage location
- \`ew_pool_req04:set(req_pool)\` writes the \`01 04\` pool value into that stored slot

This script does not compare anything. It only stores the pool value from the request side.

### \`earthworm_pool_compare.lua\`

The second script reads the pool value from the \`01 05\` stage and compares it against what was stored earlier by \`earthworm_pool_store.lua\`.

\`\`\`lua
local bytevarlib = require("suricata.bytevar")
local flowintlib = require("suricata.flowint")

function init(sig)
 bytevarlib.map(sig, "ew_resp_pool")
 flowintlib.register("ew_pool_req04")
 local needs = {}
 needs["payload"] = tostring(true)
 return needs
end

function thread_init()
 ew_resp_pool = bytevarlib.get("ew_resp_pool")
 ew_pool_req04 = flowintlib.get("ew_pool_req04")
end

function match(args)
 local resp_pool = ew_resp_pool:value()
 if resp_pool ~= nil then
 local stored = ew_pool_req04:value()
 if stored ~= nil and stored == resp_pool then
 return 1
 end
 end
 return 0
end
\`\`\`

A few details matter here too:

- \`bytevarlib.map(sig, "ew_resp_pool")\` binds this script to the pool extracted from the \`01 05\` record
- \`ew_pool_req04:value()\` retrieves the stored \`01 04\` pool value from the same flow
- \`stored == resp_pool\` is the actual equality check that EW10 was meant to break

This script has a single job: compare the \`01 05\` pool value against the stored \`01 04\` pool value.

That split made the logic easier to read, easier to test, and easier to validate against EW7 through EW10.

![Lua pool flow for the split two-script design](${require("./article_pics/earthworm_lua_pool_flow.png")})

*The final path stores the \`01 04\` pool, compares it against the \`01 05\` pool, and only then allows the \`05 02 00 01\` stage to continue.*

## The validated result

The final validated sequence was:

- \`01 04 <pool>\`
- \`01 05 <same pool>\`
- \`05 02 00 01\`

The validation shorthand stayed:

- **EW7** = no alert
- **EW8** = alert
- **EW9** = alert
- **EW10** = no alert

EW10 stopping cleanly was the main confirmation that the logic was finally doing the right job.

## Closing thoughts

The final path was straightforward once the testing set was in place.

1. tighten vanilla EarthWorm detection back to exact 6 byte records
2. improve performance by moving away from overly busy multistage 2 byte matching
3. modify the packet creation tooling so the variant cases could be generated and tested
4. collect the PCAP set
5. use EW7 through EW10 as the test shorthand
6. test the pure rule approaches
7. fail EW10 and use that failure to correct the design
8. move to Lua
9. split the final logic into 2 scripts with clear responsibilities

That produced a cleaner Suricata path for the Pygmy Goat follow on and kept the vanilla EarthWorm path clean as well.
`,
};

export default article;
